import React, { useState } from 'react';
import { Popover, Dropdown, Input } from 'antd';
import type { MenuProps } from 'antd';
import CustomInput from './CustomInput';
import {
  CloseIcon,
  ChevronDownXSmallIcon,
  DoubleChevronDownRoleIcon,
} from '@/assets/icons/icons';
import './CustomVariableTag.css';

const { TextArea } = Input;

import {
  CUSTOM_VARIABLE_TYPE_CHOICES,
  toCustomVariableTypeName,
  type CustomVariableTypeName,
} from '@/common/collectVariableType';

// Thrown by onSave when the caller showed a usage-warning confirmation and
// the user chose not to proceed - rejecting for a real error and rejecting
// for "the user said no" need different handling on the way back out.
export const CUSTOM_VAR_EDIT_CANCELLED = 'CUSTOM_VAR_EDIT_CANCELLED';

type Props = {
  name: string;
  type: CustomVariableTypeName;
  example?: string;
  description?: string;
  onRemove?: () => void;
  // Not yet persisted server-side (e.g. an LLM-suggested variable staged by
  // "Generate instructions", or a quick-add still pending agent save) -
  // rendered with a distinct accent so the user can tell it apart from
  // already-saved custom variables.
  isPending?: boolean;
  // Called with the edited fields when the popover's Save button is
  // clicked. Without this, edits only ever update this component's own
  // local state - the parent's option list (and thus what actually gets
  // saved with the agent) never sees the change. A rejected promise keeps
  // the popover open (and the draft un-committed) so the user can retry.
  onSave?: (updated: {
    name: string;
    type: CustomVariableTypeName;
    example: string;
    description: string;
  }) => void | Promise<void>;
};

export const CustomVariableTag: React.FC<Props> = ({
  name,
  type,
  example = '',
  description = '',
  onRemove,
  isPending = false,
  onSave,
}) => {
  const [open, setOpen] = useState(false);

  const [committedName, setCommittedName] = useState(name);
  const [committedType, setCommittedType] = useState(type);
  const [committedExample, setCommittedExample] = useState(example);
  const [committedDescription, setCommittedDescription] = useState(description);

  const [draftName, setDraftName] = useState(committedName);
  const [draftType, setDraftType] = useState(committedType);
  const [draftExample, setDraftExample] = useState(committedExample);
  const [draftDescription, setDraftDescription] =
    useState(committedDescription);
  const [saving, setSaving] = useState(false);

  const openPanel = () => {
    setDraftName(committedName);
    setDraftType(committedType);
    setDraftExample(committedExample);
    setDraftDescription(committedDescription);
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    if (!onSave) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onSave({
        name: draftName,
        type: draftType,
        example: draftExample,
        description: draftDescription,
      });
      setCommittedName(draftName);
      setCommittedType(draftType);
      setCommittedExample(draftExample);
      setCommittedDescription(draftDescription);
      setOpen(false);
    } catch {
      // Leave the draft as-is (open, uncommitted) so the user can
      // reconsider or retry - onSave (the caller) is responsible for its
      // own error toast, whether this was a declined usage-warning
      // confirmation or a real failure.
    } finally {
      setSaving(false);
    }
  };

  const typeMenuItems: MenuProps['items'] = CUSTOM_VARIABLE_TYPE_CHOICES.map(
    (choice) => ({
      key: choice,
      label: choice,
    })
  );

  const panel = (
    <div
      className='customVarEditPanel'
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className='customVarEditPanel__field'>
        <div className='customVarEditPanel__label'>
          Type <span className='customVarEditPanel__required'>*</span>
        </div>
        <Dropdown
          trigger={['click']}
          menu={{
            items: typeMenuItems,
            selectedKeys: [draftType],
            onClick: ({ key }) => {
              // The menu is built from CUSTOM_VARIABLE_TYPE_CHOICES, so a key outside it means
              // the two have drifted apart and the picker would silently do nothing.
              const picked = toCustomVariableTypeName(key);
              if (!picked) {
                console.error(
                  `Type picker offered ${key}, which is not a type a custom variable can take.`
                );
                return;
              }
              setDraftType(picked);
            },
          }}
        >
          <div className='customVarEditPanel__selectBox'>
            <div className='customVarEditPanel__selectText'>{draftType}</div>
            <div className='customVarEditPanel__selectIndicator'>
              <DoubleChevronDownRoleIcon />
            </div>
          </div>
        </Dropdown>
      </div>

      <div className='customVarEditPanel__field'>
        <div className='customVarEditPanel__label'>
          Name <span className='customVarEditPanel__required'>*</span>
        </div>
        <CustomInput
          bgColor='var(--light-gray)'
          height='36px'
          borderRadius='8px'
          border='none'
          style={{ padding: '8px' }}
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
        />
      </div>

      <div className='customVarEditPanel__field'>
        <div className='customVarEditPanel__label'>
          Example <span className='customVarEditPanel__required'>*</span>
        </div>
        <CustomInput
          bgColor='var(--light-gray)'
          height='36px'
          borderRadius='8px'
          border='none'
          style={{ padding: '8px' }}
          value={draftExample}
          onChange={(e) => setDraftExample(e.target.value)}
        />
      </div>

      <div className='customVarEditPanel__field customVarEditPanel__field--textarea'>
        <div className='customVarEditPanel__label'>
          Description <span className='customVarEditPanel__required'>*</span>
        </div>
        <TextArea
          className='customVarEditPanel__textarea'
          value={draftDescription}
          onChange={(e) => setDraftDescription(e.target.value)}
          placeholder='E.g. new delivery address requested by the caller, exactly as stated and confirmed during the call'
          autoSize={false}
        />
      </div>

      <div className='customVarEditPanel__actions'>
        <button
          type='button'
          className='customVarEditPanel__cancelBtn'
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type='button'
          className='customVarEditPanel__saveBtn'
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );

  return (
    <span
      className={`customVarTag${isPending ? ' customVarTag--pending' : ''}`}
    >
      <Popover
        trigger='click'
        open={open}
        onOpenChange={(next) => (next ? openPanel() : setOpen(false))}
        content={panel}
        overlayClassName='customVarEditPanel__overlay'
        placement='bottomLeft'
        arrow={false}
      >
        <span
          className='customVarTag__mainArea'
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <span className='customVarTag__segment'>{committedName}</span>
          <span className='customVarTag__divider' />
          <span className='customVarTag__typeSegment'>
            <span className='customVarTag__segment customVarTag__segment--type'>
              {committedType}
            </span>
            <span className='customVarTag__iconTrailing'>
              <ChevronDownXSmallIcon
                fill={isPending ? 'var(--text-secondary)' : 'white'}
              />
            </span>
          </span>
        </span>
      </Popover>
      {onRemove && (
        <>
          <span className='customVarTag__divider' />
          <span className='customVarTag__closeHit' onClick={onRemove}>
            <span className='customVarTag__iconTrailing'>
              <CloseIcon />
            </span>
          </span>
        </>
      )}
    </span>
  );
};

export default CustomVariableTag;
